/* ride.parts product cycler. Custom element <ride-cycler>, plus the hero
   follower that listens for the 'ride:select' event this file dispatches.
   Placement maths taken from the design handoff, section "2. Product cycler".
   No dependencies, no modules. */
(function () {
  if (typeof window === 'undefined') return;

  var BASE_W = 280; // designed card width
  var STEP_FANNED = 175; // designed fanned translate step (62.5% of the card width)
  var STEP_FLAT = 300; // designed flat translate step
  var ROW_H = 54; // details row height, so the centre card is card width + 54
  var DROP = 26; // vertical offset per step in the fanned variant
  var CONTROLS_H = 36; // the round prev and next buttons
  var CONTROLS_GAP = 20; // clear space between the cards and the controls row
  var AUTOPLAY_MS = 3500;
  var SWIPE_PX = 40; // sideways drag needed to step the cycler

  /* Direction lock. The first DRAG_LOCK_PX of a finger movement only decide
     which way the gesture goes. Sideways wins only if it beats upright by
     AXIS_RATIO, so a scroll that drifts a little sideways still scrolls. */
  var DRAG_LOCK_PX = 10;
  var AXIS_RATIO = 1;
  var FLICK_MS = 300; // a gesture shorter than this counts as a flick
  var FLICK_PX = 20; // and a flick this long steps the cycler

  /* Render order slot for the cards. The front card sits at the base and every
     card further from the centre is pushed back. Keep in step with
     --ride-z-cycler-card-base in sections/ride-product-cycler.liquid. */
  var Z_CARD_BASE = 2000;
  var Z_CARD_DEPTH = 1000; // z steps per card of distance, fine enough to never tie
  var Z_SIDE_BIAS = 1; // the right hand card of a mirrored pair sits one step back
  var CENTRE_BAND = 0.5; // a card nearer than this to the middle is the centre card

  var SELECT_EVENT = 'ride:select';
  var HERO_FADE_MS = 700;
  var DECODE_TIMEOUT_MS = 1500;

  function reduced() {
    return !!(
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    );
  }

  /* ------------------------------------------------------------------ *
   * Cycler
   * ------------------------------------------------------------------ */

  /* Element split:
     .ride-cycler__slot carries the PLACEMENT transform (--ride-cy-x/y/r/s) with the
     .6s ease, and it is the element that holds data-index, data-url and the
     data-hero-* attributes. The card rendered inside it carries only the CSS hover
     scale, so the two transforms never fight. */

  if (window.customElements && !window.customElements.get('ride-cycler')) {
    class RideCycler extends HTMLElement {
      connectedCallback() {
        if (this.ready) return;
        this.ready = true;

        this.index = 0;
        this.userActed = false;
        this.hovered = false;
        this.timer = null;

        this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-index]'));
        this.counter = this.querySelector('[data-ride-counter]');
        this.prevBtn = this.querySelector('[data-ride-prev]');
        this.nextBtn = this.querySelector('[data-ride-next]');
        if (!this.cards.length) return;

        if (this.prevBtn) {
          this.prevBtn.addEventListener('click', () => this.manual(this.index - 1));
        }
        if (this.nextBtn) {
          this.nextBtn.addEventListener('click', () => this.manual(this.index + 1));
        }

        /* Swipe with a direction lock, the method every established carousel
           uses (Swiper, Flickity, Embla). The gesture is undecided until the
           finger has moved DRAG_LOCK_PX. A mostly sideways move locks to the
           cycler and every later touchmove is prevented, so the page cannot
           scroll under the finger. A mostly upright move is handed to the page
           for good and this cycler ignores the rest of that gesture. Before the
           lock the cycler could grab a scroll half way through, which is what
           made the stack fight the page and jitter. */
        this.swipe = null;
        this.swiped = false;
        this.dragFrame = 0;

        this.addEventListener('pointerdown', (event) => {
          if (event.pointerType === 'mouse' && event.button !== 0) return;
          this.swipe = {
            x: event.clientX,
            y: event.clientY,
            id: event.pointerId,
            t: event.timeStamp || Date.now(),
            dx: 0,
            axis: ''
          };
          this.swiped = false;
        });
        // A native image drag would cancel the pointer before pointerup fires.
        this.addEventListener('dragstart', (event) => event.preventDefault());
        this.addEventListener('pointermove', (event) => {
          var s = this.swipe;
          if (!s || event.pointerId !== s.id) return;
          var dx = event.clientX - s.x;
          var dy = event.clientY - s.y;
          if (!s.axis) {
            if (Math.abs(dx) < DRAG_LOCK_PX && Math.abs(dy) < DRAG_LOCK_PX) return;
            if (Math.abs(dx) <= Math.abs(dy) * AXIS_RATIO) {
              // Upright gesture: the page keeps it, this cycler is out.
              this.swipe = null;
              return;
            }
            s.axis = 'x';
            this.swiped = true;
            this.stopAutoplay();
            this.classList.add('is-dragging');
            if (this.setPointerCapture) {
              try { this.setPointerCapture(event.pointerId); } catch (e) { /* ignore */ }
            }
          }
          s.dx = dx;
          if (this.dragFrame) return;
          this.dragFrame = window.requestAnimationFrame(() => {
            this.dragFrame = 0;
            if (this.swipe && this.swipe.axis === 'x') this.render(this.swipe.dx / (this.step || STEP_FANNED));
          });
        });
        /* A pointermove cannot stop a page scroll, a touchmove can, and the
           listener has to be non passive to be allowed to. */
        this.addEventListener('touchmove', (event) => {
          if (this.swipe && this.swipe.axis === 'x' && event.cancelable) event.preventDefault();
        }, { passive: false });
        var endSwipe = (event) => {
          var s = this.swipe;
          if (!s || event.pointerId !== s.id) return;
          this.swipe = null;
          if (this.dragFrame) { window.cancelAnimationFrame(this.dragFrame); this.dragFrame = 0; }
          this.classList.remove('is-dragging');
          if (s.axis !== 'x') return; // never locked to the cycler, so never a swipe
          var dx = s.dx;
          var step = this.step || STEP_FANNED;
          var ms = (event.timeStamp || Date.now()) - s.t;
          var flick = ms < FLICK_MS && Math.abs(dx) >= FLICK_PX;
          if (!flick && Math.abs(dx) < Math.min(SWIPE_PX, step / 3)) { this.render(); return; }
          this.swiped = true;
          this.manual(dx < 0 ? this.index + 1 : this.index - 1);
        };
        this.addEventListener('pointerup', endSwipe);
        this.addEventListener('pointercancel', () => {
          this.swipe = null;
          this.classList.remove('is-dragging');
          this.render();
        });

        this.cards.forEach((card, k) => {
          card.addEventListener('click', (event) => {
            if (this.swiped) {
              this.swiped = false;
              event.preventDefault();
              return;
            }
            var d = this.delta(k);
            if (d === 0) {
              // Centre card: the details row holds real links (Buy). Anything else
              // on the card opens the product page.
              if (event.target.closest && event.target.closest('a')) return;
              var url = card.getAttribute('data-url');
              if (url) window.location.href = url;
              return;
            }
            event.preventDefault();
            this.manual(d < 0 ? this.index - 1 : this.index + 1);
          });
        });

        this.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            this.manual(this.index - 1);
          } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            this.manual(this.index + 1);
          }
        });

        this.addEventListener('mouseenter', () => {
          this.hovered = true;
        });
        this.addEventListener('mouseleave', () => {
          this.hovered = false;
        });

        window.addEventListener('load', () => { this.measure(); this.render(); });
        this.onResize = () => {
          this.measure();
          this.render();
        };
        window.addEventListener('resize', this.onResize, { passive: true });

        this.measure();
        this.render();
        // The first render is a jump, not a transition: is-ready turns the
        // transitions back on once the cards are in place.
        var ready = () => {
          if (this.classList.contains('is-ready')) return;
          this.classList.add('is-ready');
          this.measure();
          this.render();
        };
        window.requestAnimationFrame(ready);
        // Background tabs on phones can hold animation frames back for a long
        // time; a timer makes sure the stack never stays stuck on one card.
        window.setTimeout(ready, 120);
        if (document.fonts && document.fonts.ready) {
          document.fonts.ready.then(() => this.measure());
        }
        this.startAutoplay();
      }

      disconnectedCallback() {
        if (this.onResize) window.removeEventListener('resize', this.onResize);
        this.stopAutoplay();
      }

      /* Signed distance from the current index, wrapped to -n/2 .. n/2 */
      delta(k) {
        var n = this.cards.length;
        var d = (((k - this.index) % n) + n) % n;
        if (d > n / 2) d -= n;
        return d;
      }

      fan() {
        return this.getAttribute('data-fan') === 'flat' ? 'flat' : 'fanned';
      }

      /* Measure the real card width so the translate step scales on narrow screens:
         about 62% of the card width, which is the designed 175 of 280. */
      measure() {
        // offsetWidth, not getBoundingClientRect: the slots carry a rotate/scale
        // transform, so the client rect would grow a little on every resize.
        var w = this.cards[0] ? this.cards[0].offsetWidth : BASE_W;
        if (!w) w = BASE_W;
        this.cardW = w;
        this.step = (w * (this.fan() === 'flat' ? STEP_FLAT : STEP_FANNED)) / BASE_W;
        this.style.setProperty('--ride-cycler-cw', w + 'px');
        this.style.setProperty('--ride-cycler-step', this.step + 'px');
        // The details row is measured per card: a fit line that wraps to two
        // lines makes the row taller than the designed 54px, and the centre card
        // must grow with it instead of cropping the text.
        var rowMax = this.measureRows();
        /* Centre card, its details row, a clear gap, then the controls row. The
           fanned drop is not counted: a side card starts DROP lower but is a
           whole details row shorter, so it never reaches below the centre one. */
        this.style.setProperty('--ride-cycler-h', w + rowMax + CONTROLS_GAP + CONTROLS_H + 'px');
      }

      /* Row heights are read from the laid out cards. Before the first render
         the cards are still piled up unstyled, so this runs again once the
         stack is ready and whenever the fonts finish loading. Values outside a
         sane band fall back to the designed 54px.

         Every card then gets the SAME row height, the tallest one. Two reasons:
         the centre card keeps one size whichever part is showing, instead of
         growing and shrinking on every step, and the space left under it before
         the controls is the same for all of them. */
      measureRows() {
        // Clear first, so what gets measured is the natural height of the row
        // and not the height this method wrote the last time it ran.
        this.cards.forEach((card) => card.style.removeProperty('--ride-cycler-row'));
        var rowMax = ROW_H;
        this.cards.forEach((card) => {
          var row = card.querySelector('.ride-card__row');
          var h = row ? row.offsetHeight : ROW_H;
          if (!h || h < ROW_H || h > ROW_H * 2.5) h = ROW_H;
          if (h > rowMax) rowMax = h;
        });
        this.cards.forEach((card) => card.style.setProperty('--ride-cycler-row', rowMax + 'px'));
        return rowMax;
      }

      /* offset: fraction of a step the cards are dragged by (positive drags
         right, towards the previous card). 0 or omitted means resting. */
      render(offset) {
        // The card width can change after the first measure (late stylesheet,
        // viewport change, fonts). Re-measure whenever it moved. Never while a
        // finger drags: reading offsetWidth forces a layout, and doing that on
        // every animation frame is what made a drag stutter.
        if (!this.swipe && this.cards[0] && this.cards[0].offsetWidth && this.cards[0].offsetWidth !== this.cardW) this.measure();
        var fanned = this.fan() === 'fanned';
        var step = this.step || STEP_FANNED;
        var n = this.cards.length;
        var f = Math.max(-1, Math.min(1, offset || 0));

        this.cards.forEach((card, k) => {
          var d = this.delta(k) + f;
          if (d > n / 2) d -= n;
          if (d < -n / 2) d += n;
          var a = Math.abs(d);
          var visible = a < 2;
          var tx = d * step;
          var ty = fanned ? a * DROP : 0;
          var rot = fanned ? d * 10 : 0;
          var sc = 1 - a * 0.1;
          var opacity = a <= 1 ? 1 : Math.max(0, 2 - a);
          var resting = f === 0;

          card.style.setProperty('--ride-cy-x', tx + 'px');
          card.style.setProperty('--ride-cy-y', ty + 'px');
          card.style.setProperty('--ride-cy-r', rot + 'deg');
          card.style.setProperty('--ride-cy-s', String(sc));
          card.style.opacity = String(opacity);
          /* Render order slot: the nearer the middle, the further forward. The
             depth is counted in hundredths so two cards can never land on the
             same value while a finger drags between them, and the card right of
             the middle is pushed one step behind its mirror on the left, so the
             pair that sits an equal distance out never ties either. Equal values
             let the browser fall back to document order, and the pair then
             swapped back and forth, which read as blinking. */
          card.style.zIndex = String(
            Math.max(0, Z_CARD_BASE - Math.round(Math.min(a, 2) * Z_CARD_DEPTH) - (d > 0 ? Z_SIDE_BIAS : 0))
          );
          card.style.pointerEvents = a <= 1 ? 'auto' : 'none';
          /* The centre card is the one nearest the middle, not only the one
             exactly at rest: during a drag the details row used to retract the
             moment a finger twitched, and the card height jumped with it. */
          card.classList.toggle('is-centre', a < CENTRE_BAND);
          card.classList.toggle('is-visible', visible);
          card.setAttribute('aria-hidden', a <= 1 ? 'false' : 'true');

          var links = card.querySelectorAll('a');
          for (var j = 0; j < links.length; j++) {
            if (resting && d === 0) links[j].removeAttribute('tabindex');
            else links[j].setAttribute('tabindex', '-1');
          }
        });

        if (this.counter) {
          this.counter.textContent = this.index + 1 + ' / ' + this.cards.length;
        }
      }

      /* ---- index ---- */

      go(k) {
        var n = this.cards.length;
        this.index = ((k % n) + n) % n;
        this.render();
        this.emitSelect();
      }

      emitSelect() {
        var card = this.cards[this.index];
        if (!card) return;
        document.dispatchEvent(
          new CustomEvent(SELECT_EVENT, {
            bubbles: true,
            detail: {
              index: this.index,
              heroSrc: card.getAttribute('data-hero-src') || '',
              heroSrcset: card.getAttribute('data-hero-srcset') || '',
              heroAlt: card.getAttribute('data-hero-alt') || '',
              heroPosition: card.getAttribute('data-hero-position') || '50% 50%',
              heading: card.getAttribute('data-hero-heading') || '',
              text: card.getAttribute('data-hero-text') || ''
            }
          })
        );
      }

      /* Any manual interaction stops autoplay for good, per the handoff. */
      manual(k) {
        this.userActed = true;
        this.stopAutoplay();
        this.go(k);
      }

      startAutoplay() {
        if (this.getAttribute('data-autoplay') !== 'true') return;
        if (this.userActed || this.timer) return;
        if (reduced()) return;
        this.timer = window.setInterval(() => {
          if (this.hovered || document.hidden) return;
          this.go(this.index + 1);
        }, AUTOPLAY_MS);
      }

      stopAutoplay() {
        if (this.timer) {
          window.clearInterval(this.timer);
          this.timer = null;
        }
      }
    }

    window.customElements.define('ride-cycler', RideCycler);
  }

  /* ------------------------------------------------------------------ *
   * Hero follower. Independent of the cycler: if no cycler is on the page
   * nothing ever fires and the hero keeps its own content.
   * ------------------------------------------------------------------ */

  function initHero() {
    var panel = document.querySelector('.ride-hero__panel');
    if (!panel || panel.rideHeroReady) return;
    var imgs = panel.querySelectorAll('.ride-hero__img');
    if (imgs.length < 2) return;
    panel.rideHeroReady = true;

    var current = imgs[0];
    var next = imgs[1];
    var headingEl = panel.querySelector('.ride-h1');
    var textEl = panel.querySelector('.ride-hero__sub');

    var defaults = {
      src: current.getAttribute('src') || '',
      srcset: current.getAttribute('srcset') || '',
      alt: current.getAttribute('alt') || '',
      style: current.getAttribute('style') || '',
      heading: headingEl ? headingEl.textContent : '',
      text: textEl ? textEl.textContent : ''
    };
    /* The zoom, zoom anchor and position settings were tuned for the hero's own
       image. A product's hero image gets no zoom and a centred crop, otherwise a
       square studio shot is blown up into a blurry patch. */
    // A product's hero image uses the focal point set on the file (data-hero-position).
    function swappedStyle(position) { return 'object-position: ' + (position || '50% 50%') + '; transform: none;'; }

    var token = 0;

    function swapText(el, value) {
      if (!el) return;
      if (el.textContent === value) return;
      if (reduced()) {
        el.textContent = value;
        return;
      }
      el.classList.add('is-swapping');
      window.setTimeout(function () {
        el.textContent = value;
        el.classList.remove('is-swapping');
      }, 250);
    }

    /* Flip which layer is live. The --next class holds opacity 0 / blur(14px),
       so adding and removing it IS the crossfade: the CSS transition does the
       rest. Roles swap immediately, the references after the fade. */
    function flip(outgoing, incoming) {
      outgoing.classList.add('ride-hero__img--next');
      outgoing.setAttribute('aria-hidden', 'true');
      incoming.classList.remove('ride-hero__img--next');
      incoming.removeAttribute('aria-hidden');
      current = incoming;
      next = outgoing;
    }

    function swapImage(src, srcset, alt, position) {
      if (!src || src === current.getAttribute('src')) return;
      var mine = ++token;
      next.setAttribute('src', src);
      if (srcset) next.setAttribute('srcset', srcset);
      else next.removeAttribute('srcset');
      next.setAttribute('alt', alt || defaults.alt);
      next.setAttribute('style', src === defaults.src ? defaults.style : swappedStyle(position));

      var settled = false;
      var ready = next.decode
        ? next.decode().catch(function () {})
        : new Promise(function (res) {
            next.addEventListener('load', res, { once: true });
            next.addEventListener('error', res, { once: true });
          });

      Promise.race([
        ready,
        new Promise(function (res) {
          window.setTimeout(res, DECODE_TIMEOUT_MS);
        })
      ]).then(function () {
        if (mine !== token || settled) return;
        settled = true;
        var outgoing = current;
        var incoming = next;
        if (reduced()) {
          incoming.classList.add('is-instant');
          outgoing.classList.add('is-instant');
        }
        flip(outgoing, incoming);
        window.setTimeout(function () {
          incoming.classList.remove('is-instant');
          outgoing.classList.remove('is-instant');
        }, reduced() ? 0 : HERO_FADE_MS);
      });
    }

    document.addEventListener(SELECT_EVENT, function (event) {
      var d = event.detail || {};
      swapImage(d.heroSrc || defaults.src, d.heroSrcset || defaults.srcset, d.heroAlt, d.heroPosition);
      swapText(headingEl, d.heading || defaults.heading);
      swapText(textEl, d.text || defaults.text);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initHero);
  } else {
    initHero();
  }
})();
